import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useAnalyticsPageView, useLanguageFlags, useViewport } from '@chilecompra/react-kit'
import { Button, Typography } from '@chilecompra/react-kit/components'
import routes from '../../config/settings/routes'
import { REACT_APP_MERCADO_PUBLICO_PROVEEDOR_URL } from '../../config/settings/environment'
import {
  ButtonConsiderationText,
  DesktopNavigation,
  Divided,
  Instructions,
  Item,
  ItemBody,
  ItemCollapseIcon,
  ItemExpandIcon,
  ItemHeader,
  ItemTitle,
  ItemToggle,
  MobileNavigation,
  Root,
  Section,
  Title
} from './RegisterWizardConsiderations.styles'

/**
 * The RegisterWizardConsiderations' container.
 */
const RegisterWizardConsiderations = props => {
  const { onChangeStep } = props

  const { hasLanguage } = useLanguageFlags()
  const { size } = useViewport()
  const { business } = useSelector(state => state.registerWizard)
  const [item, setItem] = useState(0)

  const handleGoToPayment = () => {
    const URL = `${REACT_APP_MERCADO_PUBLICO_PROVEEDOR_URL}${routes.paymentFromRegister}?dni=${business.dni}&foreign=${hasLanguage}`

    window.open(URL, '_blank', 'noreferrer')
  }

  const handleItemChange = updatedIndex => () => {
    setItem(currentIndex => {
      if (currentIndex === updatedIndex) return 0

      return updatedIndex
    })
  }

  const handleNext = useCallback(() => onChangeStep('complete'), [onChangeStep])

  const validateOOPP = () => {
    if (business?.isPublicOrg !== 1) {
      return (
        <>
          <Divided margin="58px 0 38px" width={size.isTiny ? '107%' : '90%'} />

          <Section item lg={11} md={6} xs={12}>
            <Instructions> Pago de Acreditación (opcional) </Instructions>

            <Typography color="black1" margin="15px 0 0" variant="body2">
              El pago de la tarifa te permitirá acreditar en línea la situación de la empresa. No es obligatorio pero
              ten ten presente que los organismos públicos pueden solicitar en las Bases de Licitación que los
              acreditados al momento de ser contratados.
            </Typography>
          </Section>

          <Section item>
            <Button color="primary" margin="32px 0" onClick={handleGoToPayment} variant="outlined">
              Ir a pagar acreditación
            </Button>
          </Section>

          <Divided margin="21px 0 112px" width="90%" />
        </>
      )
    }

    return <></>
  }

  useAnalyticsPageView('/paso/5/consideraciones')

  return (
    <Root>
      <Section item>
        <Title>Últimas consideraciones importantes antes de ingresar</Title>
      </Section>

      <Section item margin="23px 0 0" maxWidth="929px">
        <Instructions>
          Ya estás listo para convertirte en un proveedor de bienes y servicios del Estado. Podrás revisar oportunidades
          de negocio, participar en licitaciones y enviar cotizaciones a todos los organismos públicos del país.
        </Instructions>
      </Section>

      <Section item margin="23px 0 33px" maxWidth="929px">
        <Instructions>Para participar en cada proceso deberás declarar que tu empresa no está sujeta a:</Instructions>
      </Section>

      <Section item maxWidth="694px">
        <Item onClick={handleItemChange(1)}>
          <ItemHeader>
            <ItemTitle>Condenas por prácticas antisindicales</ItemTitle>

            <ItemToggle>{item !== 1 ? <ItemCollapseIcon /> : <ItemExpandIcon />}</ItemToggle>
          </ItemHeader>

          <ItemBody in={item === 1}>
            Quedarán inhabilitados para participar en licitaciones quienes tengan condenas por prácticas antisindicales
            o infracción a los derechos fundamentales del trabajador.
          </ItemBody>
        </Item>

        <Item onClick={handleItemChange(2)}>
          <ItemHeader>
            <ItemTitle>Condenas por delitos concursales</ItemTitle>

            <ItemToggle>{item !== 2 ? <ItemCollapseIcon /> : <ItemExpandIcon />}</ItemToggle>
          </ItemHeader>

          <ItemBody in={item === 2}>
            Quedarán inhabilitados para participar en licitaciones quienes tengan condenas por los delitos concursales
            establecidos en los artículos 463 y siguientes del código penal.
          </ItemBody>
        </Item>

        <Item onClick={handleItemChange(3)}>
          <ItemHeader>
            <ItemTitle>Vínculos con los funcionarios directivos</ItemTitle>

            <ItemToggle>{item !== 3 ? <ItemCollapseIcon /> : <ItemExpandIcon />}</ItemToggle>
          </ItemHeader>

          <ItemBody in={item === 3}>
            No deben haber vínculos entre el proveedor y los funcionarios directivos de la entidad compradora.
          </ItemBody>
        </Item>

        <Item onClick={handleItemChange(4)}>
          <ItemHeader>
            <ItemTitle>Delitos de cohecho</ItemTitle>

            <ItemToggle>{item !== 4 ? <ItemCollapseIcon /> : <ItemExpandIcon />}</ItemToggle>
          </ItemHeader>

          <ItemBody in={item === 4}>
            Las personas jurídicas procesadas por delitos de cohecho, lavado de activos, financiamiento del terrorismo y
            delitos contemplados por la Ley General de Pesca y Acuicultura, cometidos por sus dueños, controladores,
            responsables, ejecutivos principales, representantes o administradores, tienen prohibición de celebrar
            contratos con el Estado.
          </ItemBody>
        </Item>

        <Item onClick={handleItemChange(5)}>
          <ItemHeader>
            <ItemTitle>Condenas por Libre Competencia</ItemTitle>

            <ItemToggle>{item !== 5 ? <ItemCollapseIcon /> : <ItemExpandIcon />}</ItemToggle>
          </ItemHeader>

          <ItemBody in={item === 5}>
            Quedarán inhabilitados para participar en contratos con el Estado quienes tengan condenas por el TDLC por
            conductas contrarias a la libre competencia.
            <br />
            Quedarán inhabilitados quienes sean condenados por el TDLC a la prohibición de contratar con el Estado por
            conductas contrarias a la libre competencia.
          </ItemBody>
        </Item>
      </Section>
      {validateOOPP()}

      {!size.isTiny && (
        <Section container margin="37px 0 0 0">
          <DesktopNavigation>
            <Button margin="0 95px 0" type="button" variant="text" width="129px">
              Atrás
            </Button>

            <Button color="primary" onClick={handleNext} type="button" width="25rem">
              <ButtonConsiderationText>Omitir e ir a iniciar sesión en Mercado Público</ButtonConsiderationText>
            </Button>
          </DesktopNavigation>
        </Section>
      )}

      {size.isTiny && (
        <MobileNavigation>
          <Button type="button" variant="text" width="129px">
            Atrás
          </Button>

          <Button color="primary" onClick={handleNext} type="button" width="25rem">
            <ButtonConsiderationText>Omitir e ir a iniciar sesión en Mercado Público</ButtonConsiderationText>
          </Button>
        </MobileNavigation>
      )}
    </Root>
  )
}

RegisterWizardConsiderations.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardConsiderations.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardConsiderations
